import { useDispatch, useSelector } from "react-redux";

import {
  Box,
  ContextMenuButton,
  ContextMenuLink,
  Divider,
  Icon,
  Inline,
  Link,
  Stack,
  Text,
  TextHoverMenu
} from "@seneca/senekit";

import { linkToRegionModal } from "common/links";

import {
  getRegion,
  getUserAccountType,
  getUserGivenName,
  getUserNickName
} from "seneca-common/features/user/state";
import { openWindowSecurely } from "seneca-common/utils/routing/index";

import useUpdateApp from "features/app/hooks/useUpdateApp";
import { RocketEmoji } from "features/common/emoji";
import DarkModeMenuButton from "features/dark-mode/components/DarkModeMenuButton";
import { linkToAddCoursesWithQueryParams } from "features/dashboard/pages/courses-page/links";
import { useIsPlurallApp } from "features/plurall";
import { getLinkToPlurallStore } from "features/plurall/links";
import { isSchoolAnalyticsEnabled } from "features/regions/selectors";
import {
  linkToClassesPage,
  linkToInviteParentsPage,
  linkToPersonalInformation,
  linkToProgress,
  linkToResourceDiscoverTeacher,
  linkToResourcesHub,
  linkToTeacherHelpSection,
  linkToTeacherHomepage,
  linkToSchoolPage
} from "features/teachers/links";
import {
  linkToTeacherFeedback,
  linkToTeacherWhatIsPremiumPage
} from "features/teachers/routes/links";
import { setPlurallHelpCentreModalOpen } from "features/ui-state/actions";

import useGetTeacherNavbarProps, {
  TeacherNavbarProps
} from "../../hooks/useGetTeacherNavbarProps";

export default function TeacherNavbarDesktop() {
  const hoverMenuProps = useGetTeacherNavbarProps();
  const schoolAnalyticsEnabled = useSelector(isSchoolAnalyticsEnabled);
  const region = useSelector(getRegion);

  return (
    <Inline space="xlarge">
      <Link tone="neutral" navLink to={linkToTeacherHomepage()}>
        Home
      </Link>
      <Link tone="neutral" navLink to={linkToClassesPage()}>
        Classes
      </Link>
      {region === "GB" && (
        <Link tone="neutral" navLink to={linkToSchoolPage()}>
          School
        </Link>
      )}
      {schoolAnalyticsEnabled && (
        <Link tone="neutral" navLink to={linkToProgress()}>
          Reports
        </Link>
      )}

      <TeacherDesktopHoverMenu {...hoverMenuProps} />
    </Inline>
  );
}

function TeacherDesktopHoverMenu({
  email,
  locale,
  signOutOnClick,
  sellPremiumToUser
}: TeacherNavbarProps) {
  const dispatch = useDispatch();
  const isPlurallApp = useIsPlurallApp();
  const givenName = useSelector(getUserGivenName);
  const nickname = useSelector(getUserNickName);
  const role = useSelector(getUserAccountType);
  const name = role === "schoolteacher" && nickname ? nickname : givenName;
  const { showInviteParents } = useGetTeacherNavbarProps();
  const {
    update: updateAppVersion,
    ready: appVersionUpdateReady,
    updating: appVersionIsUpdating
  } = useUpdateApp();

  return (
    <TextHoverMenu position="bottomLeft" ariaLabel="More navigation options">
      {{
        label: (
          <Text dataNoTranslate size="medium" weight="bold" truncate>
            {name}
          </Text>
        ),
        options: (
          <Box width="220px" margin="large">
            <Stack space="large">
              {!isPlurallApp && (
                <Box margin="large">
                  <Text size="small" tone="neutral" truncate>
                    {email}
                  </Text>
                </Box>
              )}
              <Divider direction="horizontal" />

              <Stack>
                <ContextMenuLink to={linkToPersonalInformation()}>
                  {{
                    icon: (
                      <Icon
                        iconName="cog"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Settings</Text>
                  }}
                </ContextMenuLink>

                <ContextMenuLink
                  to={linkToAddCoursesWithQueryParams({
                    price: "Free"
                  })}
                >
                  {{
                    icon: (
                      <Icon
                        iconName="graduationCap"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Student Platform</Text>
                  }}
                </ContextMenuLink>

                <DarkModeMenuButton />

                {showInviteParents && (
                  <ContextMenuButton
                    onClick={() =>
                      openWindowSecurely(linkToInviteParentsPage(locale))
                    }
                  >
                    {{
                      icon: (
                        <Icon
                          iconName="plus"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: <Text>Invite parents</Text>
                    }}
                  </ContextMenuButton>
                )}

                {!isPlurallApp && (
                  <ContextMenuLink to={linkToRegionModal()}>
                    {{
                      icon: (
                        <Icon
                          iconName="globe"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: <Text>Region</Text>
                    }}
                  </ContextMenuLink>
                )}

                {!isPlurallApp && (
                  <ContextMenuLink to={linkToTeacherFeedback()}>
                    {{
                      icon: (
                        <Icon
                          iconName="megaphone"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: <Text>Feedback</Text>
                    }}
                  </ContextMenuLink>
                )}

                <ContextMenuButton
                  onClick={() =>
                    isPlurallApp
                      ? dispatch(setPlurallHelpCentreModalOpen())
                      : openWindowSecurely(linkToTeacherHelpSection(locale))
                  }
                >
                  {{
                    icon: (
                      <Icon
                        iconName="help"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Help centre</Text>
                  }}
                </ContextMenuButton>

                {!isPlurallApp && (
                  <ContextMenuLink to={linkToResourcesHub()}>
                    {{
                      icon: (
                        <Icon
                          iconName="star"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: <Text>News & CPD</Text>
                    }}
                  </ContextMenuLink>
                )}

                {!isPlurallApp && (
                  <ContextMenuLink to={linkToResourceDiscoverTeacher()}>
                    {{
                      icon: (
                        <Icon
                          iconName="search"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: <Text>Resource search</Text>
                    }}
                  </ContextMenuLink>
                )}

                {sellPremiumToUser && !isPlurallApp && (
                  <ContextMenuLink to={linkToTeacherWhatIsPremiumPage()}>
                    {{
                      icon: <RocketEmoji />,
                      text: <Text>What's premium?</Text>
                    }}
                  </ContextMenuLink>
                )}

                {sellPremiumToUser && isPlurallApp && (
                  <ContextMenuButton
                    onClick={() => openWindowSecurely(getLinkToPlurallStore())}
                  >
                    {{
                      icon: <RocketEmoji />,
                      text: <Text>What's premium?</Text>
                    }}
                  </ContextMenuButton>
                )}
              </Stack>

              <Divider direction="horizontal" />

              {appVersionUpdateReady && (
                <>
                  <ContextMenuButton onClick={updateAppVersion}>
                    {{
                      icon: (
                        <Icon
                          iconName="reassign"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: (
                        <Text>
                          {appVersionIsUpdating ? (
                            <>Updating...</>
                          ) : (
                            <>Update Seneca</>
                          )}
                        </Text>
                      )
                    }}
                  </ContextMenuButton>

                  <Divider direction="horizontal" />
                </>
              )}

              <ContextMenuButton onClick={signOutOnClick}>
                {{
                  icon: (
                    <Icon
                      iconName="leave"
                      size="medium"
                      accessibilityLabel={null}
                      tone="neutral"
                    />
                  ),
                  text: <Text>Logout</Text>
                }}
              </ContextMenuButton>
            </Stack>
          </Box>
        )
      }}
    </TextHoverMenu>
  );
}
